import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { WarningCircle } from "@phosphor-icons/react";

export function FieldShell({
  label,
  hint,
  error,
  currentLength,
  maxLength,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  currentLength?: number;
  maxLength?: number;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[#F5F7F7]">
          {label}
        </span>
        {currentLength !== undefined && (
          <span
            className={`field-counter-badge ${
              maxLength && currentLength > maxLength
                ? "border-red-500/40 text-red-400 bg-red-500/10"
                : ""
            }`}
          >
            {currentLength} {maxLength ? `/ ${maxLength}` : "chars"}
          </span>
        )}
      </div>
      {children}
      {hint && (
        <span className="mt-1.5 block text-xs leading-5 text-[#70777B]">
          {hint}
        </span>
      )}
      {error && (
        <span
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-xs text-red-300 font-medium"
          role="alert"
        >
          <WarningCircle size={14} className="shrink-0 text-red-400" />
          {error}
        </span>
      )}
    </label>
  );
}

const fieldClass =
  "liquid-glass-field w-full rounded-xl border border-white/[.12] bg-white/[.045] px-4 py-3 text-[15px] text-[#F5F7F7] placeholder:text-[#70777B] transition-all duration-200 focus:border-brand/70 focus:bg-white/[.07] focus:outline-none focus:ring-2 focus:ring-brand/20 focus:shadow-[0_0_18px_rgba(168,230,29,0.12)]";

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className = "", ...props }, ref) => (
  <input ref={ref} className={`${fieldClass} ${className}`} {...props} />
));

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className = "", ...props }, ref) => (
  <textarea
    ref={ref}
    className={`${fieldClass} min-h-28 resize-y ${className}`}
    {...props}
  />
));

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(({ className = "", ...props }, ref) => (
  <select ref={ref} className={`${fieldClass} ${className}`} {...props} />
));

Input.displayName = "Input";
Textarea.displayName = "Textarea";
Select.displayName = "Select";

